import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, Clock, MapPin, Camera, Send, CheckCircle2 } from "lucide-react";
import { Reveal, Overline } from "./Reveal";
import { API } from "../lib/api";
import { PHONE_DISPLAY, PHONE_TEL, EMAIL, PROPERTY_TYPES, SERVICE_TYPES } from "../lib/data";

const EMPTY = {
  name: "",
  phone: "",
  email: "",
  address: "",
  property_type: PROPERTY_TYPES[0],
  service_type: SERVICE_TYPES[0],
  message: "",
};

const inputCls =
  "w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-800 placeholder-slate-400 outline-none transition focus:border-sky-400 focus:ring-2 focus:ring-sky-200";

const INFO = [
  { icon: Phone, label: "Téléphone", value: PHONE_DISPLAY, href: PHONE_TEL, id: "phone" },
  { icon: Mail, label: "Email", value: EMAIL, href: `mailto:${EMAIL}`, id: "email" },
  { icon: Clock, label: "Horaires", value: "Lun – Sam : 8h à 19h", id: "hours" },
  { icon: MapPin, label: "Secteur", value: "Lille & Métropole lilloise", id: "area" },
];

export const QuoteForm = () => {
  const [form, setForm] = useState(EMPTY);
  const [photo, setPhoto] = useState(null);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      if (photo) fd.append("photo", photo);
      await axios.post(`${API}/quotes`, fd);
      setSent(true);
      setForm(EMPTY);
      setPhoto(null);
      toast.success("Demande envoyée ! Nous vous recontactons sous 24h.");
    } catch (err) {
      toast.error("Une erreur est survenue. Réessayez ou appelez-nous directement.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="devis" data-testid="quote-form-section" className="py-20 md:py-28 bg-sky-50/50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 grid lg:grid-cols-5 gap-12 lg:gap-16">
        <Reveal className="lg:col-span-2">
          <Overline>Contact & Devis</Overline>
          <h2 id="contact" className="mt-4 font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-900 scroll-mt-24">
            Votre devis gratuit en 2 minutes
          </h2>
          <p className="mt-4 text-base text-slate-600 leading-relaxed">
            Décrivez votre besoin, ajoutez une photo si vous le souhaitez : nous vous rappelons
            sous 24h avec un prix ferme et sans engagement.
          </p>
          <div className="mt-8 space-y-5">
            {INFO.map((item) => (
              <div key={item.id} data-testid={`contact-info-${item.id}`} className="flex items-center gap-4">
                <span className="w-11 h-11 rounded-2xl bg-white border border-sky-100 text-sky-600 flex items-center justify-center shadow-sm shrink-0">
                  <item.icon size={18} />
                </span>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">{item.label}</p>
                  {item.href ? (
                    <a href={item.href} className="text-sm font-semibold text-slate-800 hover:text-sky-600 transition-colors">
                      {item.value}
                    </a>
                  ) : (
                    <p className="text-sm font-semibold text-slate-800">{item.value}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </Reveal>

        <Reveal delay={0.15} className="lg:col-span-3">
          <div className="rounded-3xl bg-white border border-slate-100 shadow-xl shadow-sky-900/10 p-7 sm:p-10">
            {sent ? (
              <div data-testid="quote-form-success" className="text-center py-14">
                <CheckCircle2 size={56} className="mx-auto text-emerald-500" />
                <h3 className="mt-5 font-heading text-2xl font-bold text-slate-900">Demande bien reçue !</h3>
                <p className="mt-2 text-sm text-slate-600 max-w-sm mx-auto">
                  Merci pour votre confiance. Un membre de l'équipe WM Clean vous rappelle sous 24h
                  ouvrées avec votre devis personnalisé.
                </p>
                <button
                  data-testid="quote-form-new-request-button"
                  onClick={() => setSent(false)}
                  className="mt-7 inline-flex items-center gap-2 rounded-full border border-sky-200 text-sky-700 font-semibold px-6 py-3 text-sm hover:bg-sky-50 transition-colors"
                >
                  Envoyer une autre demande
                </button>
              </div>
            ) : (
              <form onSubmit={submit} data-testid="quote-form" className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label htmlFor="qf-name" className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5">Nom *</label>
                  <input id="qf-name" data-testid="quote-form-name-input" required value={form.name} onChange={set("name")} placeholder="Jean Dupont" className={inputCls} />
                </div>
                <div>
                  <label htmlFor="qf-phone" className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5">Téléphone *</label>
                  <input id="qf-phone" data-testid="quote-form-phone-input" required type="tel" value={form.phone} onChange={set("phone")} placeholder="06 12 34 56 78" className={inputCls} />
                </div>
                <div>
                  <label htmlFor="qf-email" className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5">Email *</label>
                  <input id="qf-email" data-testid="quote-form-email-input" required type="email" value={form.email} onChange={set("email")} placeholder="jean@exemple.fr" className={inputCls} />
                </div>
                <div>
                  <label htmlFor="qf-address" className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5">Adresse *</label>
                  <input id="qf-address" data-testid="quote-form-address-input" required value={form.address} onChange={set("address")} placeholder="12 rue de Paris, Lille" className={inputCls} />
                </div>
                <div>
                  <label htmlFor="qf-property" className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5">Type de bien *</label>
                  <select id="qf-property" data-testid="quote-form-property-select" value={form.property_type} onChange={set("property_type")} className={inputCls}>
                    {PROPERTY_TYPES.map((t) => <option key={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <label htmlFor="qf-service" className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5">Prestation *</label>
                  <select id="qf-service" data-testid="quote-form-service-select" value={form.service_type} onChange={set("service_type")} className={inputCls}>
                    {SERVICE_TYPES.map((t) => <option key={t}>{t}</option>)}
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="qf-message" className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5">Message</label>
                  <textarea id="qf-message" data-testid="quote-form-message-input" rows={4} value={form.message} onChange={set("message")} placeholder="Nombre de fenêtres, étage, accès, fréquence souhaitée…" className={`${inputCls} resize-none`} />
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="qf-photo" className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5">Photo (optionnel)</label>
                  <label
                    htmlFor="qf-photo"
                    data-testid="quote-form-photo-dropzone"
                    className="flex items-center gap-3 rounded-xl border border-dashed border-sky-300 bg-sky-50/60 px-4 py-4 cursor-pointer hover:bg-sky-50 transition-colors"
                  >
                    <Camera size={18} className="text-sky-600 shrink-0" />
                    <span className="text-sm text-slate-600 truncate">
                      {photo ? photo.name : "Ajoutez une photo de vos vitres (8 Mo max)"}
                    </span>
                  </label>
                  <input id="qf-photo" data-testid="quote-form-photo-input" type="file" accept="image/*" className="hidden" onChange={(e) => setPhoto(e.target.files[0] || null)} />
                </div>
                <p className="sm:col-span-2 text-xs text-slate-400 leading-relaxed">
                  En envoyant ce formulaire, vous acceptez que vos données soient utilisées
                  uniquement pour traiter votre demande de devis.{" "}
                  <a href="/mentions-legales" data-testid="quote-form-privacy-link" className="text-sky-600 font-semibold hover:underline">
                    En savoir plus (RGPD)
                  </a>
                </p>
                <button
                  type="submit"
                  data-testid="quote-form-submit-button"
                  disabled={sending}
                  className="sm:col-span-2 inline-flex items-center justify-center gap-2 rounded-full bg-sky-600 hover:bg-sky-700 disabled:opacity-60 text-white font-semibold px-8 py-4 text-sm shadow-lg shadow-sky-600/25 transition-all hover:-translate-y-0.5"
                >
                  {sending ? "Envoi en cours…" : "Recevoir mon devis gratuit"} <Send size={16} />
                </button>
              </form>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
};
